import * as yup from "yup";
import {
  Default_Background_Primary_Color,
  Default_Background_Secondary_Color,
  Default_Button_Background_Color,
  Default_Button_Text_Color,
} from "../../../../../common/infrastructure/utilities/colorUtility";
import {
  emailMustBeValidEmail,
  fieldMaxLength,
  fieldMustBePositive,
  nullableNumber,
  requiredField,
} from "../../../../../common/infrastructure/helpers/yupHelper";
import {
  StudioType,
  TimeStep,
  PaymentTypes,
  CommissionsVisibility,
} from "../../../../../common/enums";
import { MAX_RESERVATION_NOTIFICATION_EMAILS } from "./reservationNotificationEmails";

/**
 * Cap on the name shown as the sender of the studio's emails. Mirrors
 * EmailSenderNameResolver.MaxLength on the backend.
 *
 * This is not cosmetic: an over-long display name makes the From header wrap
 * onto a second line, and mail servers that read only the first line reject the
 * message outright ("5.1.7 Invalid or unparseable From address"). Better to tell
 * the user here than to silently drop their branding at send time.
 */
export const MAX_EMAIL_SENDER_NAME_LENGTH = 40;

export const ACCENT_PAIR_MESSAGE = "Изберете и двата цвята на акцента";

export const studioWizardFormSchema = yup
  .object({
    id: yup.number().defined().required(),
    emailSenderName: yup
      .string()
      .nullable()
      .defined()
      .trim()
      .max(
        MAX_EMAIL_SENDER_NAME_LENGTH,
        fieldMaxLength(
          "Име на подателя в имейлите",
          MAX_EMAIL_SENDER_NAME_LENGTH,
        ),
      ),
    name: yup
      .string()
      .nullable()
      .defined()
      .required(requiredField("Име на обекта"))
      .trim()
      .max(200, fieldMaxLength("Име на обекта", 200)),
    phoneNumber: yup
      .string()
      .nullable()
      .defined()
      .required(requiredField("Телефонен номер"))
      .trim()
      .max(200, fieldMaxLength("Телефонен номер", 200)),
    phoneNumberDescription: yup
      .string()
      .nullable()
      .optional()
      .trim()
      .max(200, fieldMaxLength("Описание на телефонен номер", 200)),
    secondaryPhoneNumber: yup
      .string()
      .nullable()
      .defined()
      .optional()
      .trim()
      .max(200, fieldMaxLength("Телефонен номер 2", 200)),
    secondaryPhoneNumberDescription: yup
      .string()
      .nullable()
      .optional()
      .trim()
      .max(200, fieldMaxLength("Описание на телефонен номер 2", 200)),
    email: yup
      .string()
      .email(emailMustBeValidEmail())
      .nullable()
      .defined()
      .required(requiredField("Имейл"))
      .trim()
      .max(200, fieldMaxLength("Имейл", 200)),
    url: yup
      .string()
      .nullable()
      .defined()
      .notRequired()
      .test(
        "starts-with-https-or-http",
        "Всеки домейн трябва да започва с https:// или http://",
        (value) => {
          if (!value) return true;
          return value
            .split(",")
            .map((v) => v.trim())
            .every(
              (v) => !v || v.startsWith("https://") || v.startsWith("http://"),
            );
        },
      ),

    ownerId: yup.number().defined().required(requiredField("Собственик")),
    address: yup
      .string()
      .nullable()
      .defined()
      .required(requiredField("Адрес"))
      .trim()
      .max(200, fieldMaxLength("Адрес", 200)),

    // Empty means "search Google Maps for the address above", so unlike the address itself
    // this one is optional — but anything entered is opened on click (on the public link page
    // too), so it has to be a real http(s) link.
    addressMapUrl: yup
      .string()
      .nullable()
      .defined()
      .default("")
      .trim()
      .max(500, fieldMaxLength("Линк към локация", 500))
      .test(
        "starts-with-https-or-http",
        "Линкът към локацията трябва да започва с https:// или http://",
        (value) =>
          !value || value.startsWith("https://") || value.startsWith("http://"),
      ),
    backgroundColorPrimary: yup
      .string()
      .defined()
      .matches(/^[A-Fa-f0-9]{6}$/, "Невалиден цвят")
      .required(requiredField("Основен цвят")),

    backgroundColorSecondary: yup
      .string()
      .defined()
      .matches(/^[A-Fa-f0-9]{6}$/, "Невалиден цвят")
      .required(requiredField("Второстепенен цвят")),

    buttonBackgroundColor: yup
      .string()
      .defined()
      .matches(/^[A-Fa-f0-9]{6}$/, "Невалиден цвят")
      .required(requiredField("Цвят на фон на бутон")),

    buttonTextColor: yup
      .string()
      .defined()
      .matches(/^[A-Fa-f0-9]{6}$/, "Невалиден цвят")
      .required(requiredField("Цвят на текст на бутон")),

    // Empty means "use the studio's own button color", so unlike the four above this one is
    // optional — but anything actually entered still has to be a real color.
    linkPageButtonColor: yup
      .string()
      .defined()
      .default("")
      .matches(/^([A-Fa-f0-9]{6})?$/, "Невалиден цвят"),

    // Design Studio's accent gradient and the text on it. All empty means "no accent of
    // its own": the studio keeps the built-in purple → pink, exactly as before. The pair
    // is all or nothing: the backend saves half an accent as no accent, without a word.
    accentColorFrom: yup
      .string()
      .defined()
      .default("")
      .matches(/^([A-Fa-f0-9]{6})?$/, "Невалиден цвят"),

    accentColorTo: yup
      .string()
      .defined()
      .default("")
      .matches(/^([A-Fa-f0-9]{6})?$/, "Невалиден цвят")
      .test(
        "accent-pair",
        ACCENT_PAIR_MESSAGE,
        function (value) {
          return !!value === !!this.parent.accentColorFrom;
        },
      ),

    // Its own text colour only makes sense on an accent of its own.
    accentTextColor: yup
      .string()
      .defined()
      .default("")
      .matches(/^([A-Fa-f0-9]{6})?$/, "Невалиден цвят")
      .test(
        "accent-pair",
        ACCENT_PAIR_MESSAGE,
        function (value) {
          const { accentColorFrom, accentColorTo } = this.parent;
          return !value || (!!accentColorFrom && !!accentColorTo);
        },
      ),

    linkPageShowPhone: yup.boolean().defined().default(false),

    linkPageShowAddress: yup.boolean().defined().default(true),

    usersCanCancelReservations: yup.boolean().defined().required(),

    displayClosedReservationsLabel: yup.boolean().defined().required(),

    askForUserBirthdayOnRegistration: yup.boolean().defined().required(),

    disableGoogleLogin: yup.boolean().defined().required(),

    allowLateCancellationsWithPayment: yup.boolean().defined().required(),

    autoMarkUserAsUnreliableOnLateCancellation: yup
      .boolean()
      .defined()
      .required(),

    isTraining: yup.boolean().defined().required(),

    limitUserToOneReservationPerDay: yup.boolean().defined().required(),

    studioUsesDifferentPlanTypes: yup.boolean().defined().required(),

    usesPreReservationForms: yup.boolean().defined().required(),

    studioDoesNotUsePlans: yup.boolean().defined().required(),

    studioDoesNotUseWorkouts: yup.boolean().defined().required(),

    studioDoesNotUseBodyMetrics: yup.boolean().defined().required(),

    studioDoesNotUseGoals: yup.boolean().defined().required(),

    studioDoesNotUseServicePortfolios: yup.boolean().defined().required(),

    types: yup
      .array()
      .defined()
      .of(
        yup
          .mixed<StudioType>()
          .defined()
          .oneOf(Object.values(StudioType) as StudioType[]),
      )
      .min(1, requiredField("Тип обект")),

    timeStep: yup
      .mixed<TimeStep>()
      .defined()
      .oneOf(Object.values(TimeStep) as TimeStep[])
      .nullable(),

    // Which report calculates and shows the employees' commissions. Studios that use
    // only one of the two reports for payroll hide the other one so the numbers
    // cannot be confused.
    commissionsVisibleIn: yup
      .mixed<CommissionsVisibility>()
      .defined()
      .required()
      .oneOf(
        Object.values(CommissionsVisibility).filter(
          (x) => typeof x === "number",
        ) as CommissionsVisibility[],
      ),

    usesHalls: yup.boolean().nullable().defined().required(),
    showAvailableSeatsInPortal: yup.boolean().defined().required(),
    useGroupClassCategories: yup.boolean().defined().required(),
    showCalendarTileInPortal: yup.boolean().defined().required(),
    enableQuickArrival: yup.boolean().defined().required(),

    eventBasePrice: yup
      .number()
      .defined()
      .positive(fieldMustBePositive("Базова цена за клас"))
      .nullable(),
    eventBaseFreeSpaces: yup
      .number()
      .defined()
      .positive(fieldMustBePositive("Свободни места по подразбиране за клас"))
      .nullable(),
    minutesBeforeEventCancellationCloses: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(0)
      .positive(
        fieldMustBePositive(
          "Време преди клас, след което се затваря опцията за канселиране на събитие.",
        ),
      ),
    minutesAfterEventToMarkAsMissed: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(0)
      .positive(fieldMustBePositive("Време за маркиране на 'неявил се'")),
    reservationCloseTimeBeforeEventMinutes: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(0)
      .positive(
        fieldMustBePositive("Време, след което се затварят резервациите"),
      ),
    scheduleVisibilityDaysAhead: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(0)
      .positive(
        fieldMustBePositive(
          "Време, за което да е видим графика за потребитлите напред",
        ),
      ),
    scheduleVisibilityDaysAheadForSubscribedUsers: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(0)
      .positive(
        fieldMustBePositive(
          "Време, за което да е видим графика за потребители с абонамент напред",
        ),
      )
      .test(
        "is-greater-or-equal",
        "Стойността трябва да е по-голяма или равна на обичайната видимост на графика",
        function (value) {
          const { scheduleVisibilityDaysAhead } = this.parent;
          if (value === null || value === undefined) return true;
          if (
            scheduleVisibilityDaysAhead === null ||
            scheduleVisibilityDaysAhead === undefined
          )
            return true;
          return value >= scheduleVisibilityDaysAhead;
        },
      ),
    timezone: yup.string().defined().required(requiredField("Часова зона")),
    percentLeftAfterMultisportTaxes: yup
      .number()
      .defined()
      .positive(fieldMustBePositive("Печалба след MultiSport"))
      .nullable(),
    percentLeftAfterCoolFitTaxes: yup
      .number()
      .defined()
      .positive(fieldMustBePositive("Печалба след CoolFit"))
      .nullable(),
    supportedTypesOfPayment: yup
      .array()
      .defined()
      .of(
        yup
          .number()
          .positive(fieldMustBePositive("Поддържани начинин на плащане"))
          .required(requiredField("Поддържани начинин на плащане")),
      ),
    firstReminderHoursBefore: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(1, "Първото напомняне трябва да е поне 1 час преди събитието"),
    secondReminderHoursBefore: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(1, "Второто напомняне трябва да е поне 1 час преди събитието")
      .test(
        "is-less-than-first",
        "Второто напомняне трябва да е с по-малко часове преди събитието от първото напомняне",
        function (value) {
          const { firstReminderHoursBefore } = this.parent;
          if (value === null || value === undefined) return true;
          if (
            firstReminderHoursBefore === null ||
            firstReminderHoursBefore === undefined
          )
            return true;
          return value < firstReminderHoursBefore;
        },
      ),
    // Comma-separated on the API (StudioSettings.ReservationNotificationEmail),
    // an array here so every row gets its own input and its own error message.
    // Blank rows are allowed — the user adds a row before typing in it, and
    // leftover empty rows are dropped when the form is submitted.
    //
    // The .ensure() calls are what make "no notification email at all" a valid
    // form. The wizard always renders one recipient row, and react-hook-form
    // registers that row's Controller as `reservationNotificationEmails.0`
    // with NO value while it is untouched — which lands in the submitted data
    // as [undefined], not []. That used to fail the row's own .defined() with
    // yup's internal "reservationNotificationEmails[0] must be defined",
    // blocking save on every studio whose staff never typed a notification
    // address. .ensure() casts null/undefined to "" (row) and to [] (whole
    // field) BEFORE the tests run, so an untouched row is simply a blank one
    // and the feature stays off. It also narrows the inferred type to string /
    // string[], which is why the row no longer needs a .defined() of its own.
    reservationNotificationEmails: yup
      .array()
      .of(
        yup
          .string()
          .ensure()
          .trim()
          .test(
            "is-email-or-blank",
            "Невалиден имейл адрес",
            (value) => !value || yup.string().email().isValidSync(value),
          )
          .max(200, fieldMaxLength("Имейл за известия", 200)),
      )
      .ensure()
      .defined()
      .max(
        MAX_RESERVATION_NOTIFICATION_EMAILS,
        `Максимум ${MAX_RESERVATION_NOTIFICATION_EMAILS} имейла за известия.`,
      )
      .test(
        "no-duplicate-emails",
        "Имейл адресите не могат да се повтарят",
        (value) => {
          const filled = (value ?? [])
            .map((email) => (email ?? "").trim().toLowerCase())
            .filter((email) => email.length > 0);

          return new Set(filled).size === filled.length;
        },
      ),
    smsReminderHoursBefore: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(1, "SMS напомнянето трябва да е поне 1 час преди събитието"),
    // Device (push) reminder, free. Empty keeps it off, like the SMS one.
    pushReminderHoursBefore: yup
      .number()
      .defined()
      .transform(nullableNumber)
      .nullable()
      .min(1, "Известието на устройството трябва да е поне 1 час преди събитието"),
    smsReminderContent: yup
      .string()
      .nullable()
      .optional()
      .trim()
      // Count raw characters (incl. the {{date}}/{{time}} tokens). Each token is
      // 8 chars but expands to 5 (dd.MM / HH:mm), so the sent SMS is always ≤ the
      // raw length — a hard 70 cap guarantees a single Cyrillic SMS.
      .max(
        70,
        "SMS съдържанието надвишава 70 символа (лимит за 1 SMS с кирилица). Съкратете текста.",
      ),
  })
  .required();

export type IStudioWizardFormModel = yup.InferType<
  typeof studioWizardFormSchema
>;

/**
 * Shape the wizard GET endpoint actually returns. Identical to the form model
 * except the notification recipients arrive as one comma-separated string
 * (StudioSettings.ReservationNotificationEmail) instead of an array.
 */
export type IStudioWizardApiModel = Omit<
  IStudioWizardFormModel,
  | "reservationNotificationEmails"
  | "accentColorFrom"
  | "accentColorTo"
  | "accentTextColor"
> & {
  reservationNotificationEmail?: string | null;
  // Null until the studio applies a palette with an accent in Design Studio.
  accentColorFrom?: string | null;
  accentColorTo?: string | null;
  accentTextColor?: string | null;
  // The logo is uploaded on its own the moment it is picked, so it comes back with the studio
  // but is never part of the form the wizard submits.
  logoUrl?: string | null;
};

export const studioWizardFormDefaultValues: IStudioWizardFormModel = {
  id: 0,
  name: "",
  emailSenderName: "",
  phoneNumber: "",
  phoneNumberDescription: "",
  secondaryPhoneNumber: "",
  secondaryPhoneNumberDescription: "",
  email: "",
  url: "",
  address: "",
  addressMapUrl: "",
  ownerId: 0,
  backgroundColorPrimary: Default_Background_Primary_Color,
  backgroundColorSecondary: Default_Background_Secondary_Color,
  buttonBackgroundColor: Default_Button_Background_Color,
  buttonTextColor: Default_Button_Text_Color,
  linkPageButtonColor: "",
  accentColorFrom: "",
  accentColorTo: "",
  accentTextColor: "",
  linkPageShowPhone: false,
  linkPageShowAddress: true,
  usersCanCancelReservations: true,
  types: [StudioType.Group],
  timeStep: TimeStep.ThirtyMins,

  isTraining: false,
  limitUserToOneReservationPerDay: false,
  studioUsesDifferentPlanTypes: false,
  usesPreReservationForms: false,
  // A new studio starts without plans, workouts, body metrics and goals (the same as a
  // website signup); the owner switches each on once they need it. Editing loads the saved
  // values.
  studioDoesNotUsePlans: true,
  studioDoesNotUseWorkouts: true,
  studioDoesNotUseBodyMetrics: true,
  studioDoesNotUseGoals: true,
  // Unlike the toggles above, a new studio keeps the service portfolios.
  studioDoesNotUseServicePortfolios: false,
  commissionsVisibleIn: CommissionsVisibility.Both,
  usesHalls: false,
  showAvailableSeatsInPortal: false,
  useGroupClassCategories: false,
  showCalendarTileInPortal: false,
  enableQuickArrival: false,
  displayClosedReservationsLabel: false,
  askForUserBirthdayOnRegistration: false,
  disableGoogleLogin: false,
  allowLateCancellationsWithPayment: false,
  autoMarkUserAsUnreliableOnLateCancellation: false,
  eventBasePrice: null,
  eventBaseFreeSpaces: null,
  minutesBeforeEventCancellationCloses: null,
  minutesAfterEventToMarkAsMissed: null,
  reservationCloseTimeBeforeEventMinutes: null,
  scheduleVisibilityDaysAhead: null,
  scheduleVisibilityDaysAheadForSubscribedUsers: null,
  timezone: "",
  percentLeftAfterMultisportTaxes: null,
  percentLeftAfterCoolFitTaxes: null,
  // "Абонаментна карта" stays among the defaults even though a new studio starts without
  // plans: it is only hidden (getHiddenPaymentTypes), ready for when plans are switched on.
  supportedTypesOfPayment: [
    PaymentTypes.Card,
    PaymentTypes.Cash,
    PaymentTypes.Multisport,
    PaymentTypes.Plan,
    PaymentTypes.MyPos,
  ],
  firstReminderHoursBefore: null,
  secondReminderHoursBefore: null,
  reservationNotificationEmails: [],
  smsReminderHoursBefore: null,
  smsReminderContent: null,
  pushReminderHoursBefore: null,
};
